import PaymentsGrid from "../PaymentsGrid";
import React from "react";
import deltas from "../../../redux/actions/deltas";
import {connect} from "react-redux";
import CheckboxField from "../../atoms/CheckboxField";
import SelectInput from "../../atoms/SelectInput";
import signals from "../../../redux/actions/signals";
import roles from "../../../redux/roles";
import {createSelector} from 'reselect';
import PaymentProcessesPollingAlertMessage from "../../molecules/PaymentProcessesPollingAlertMessage";
import DownloadButton from "../../molecules/DownloadButton";
import {selectIsLoadingDownloadAllTransactions} from "../../../redux/reducers/states/downloadAllTransactions";
import {selectIsLoadingDownloadMonthlyClaim} from "../../../redux/reducers/states/downloadMonthlyClaim";
import {selectIsLoadingDownloadFinanceReport} from "../../../redux/reducers/states/downloadFinanceReport";
import {
    GRID_FILTER_TYPE_DATE,
    GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD,
    GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC,
    GRID_FILTER_TYPE_BOOLEAN,
} from "../../molecules/GridFilter";
import {OffsetPagination} from '@zendeskgarden/react-pagination';
import {ThemeProvider} from '@zendeskgarden/react-theming';
import paginationLabels from "../../molecules/paginationLabels";

const EARLIEST_TRANSACTIONS_DOWNLOAD_DATE = new Date(2020, 3); // April (0-indexed month)
const now = new Date();
let workingDate = EARLIEST_TRANSACTIONS_DOWNLOAD_DATE;
const allTransactionsDownloadDates = [];

do {
    allTransactionsDownloadDates.unshift(new Date(workingDate.getTime()));
    workingDate.setMonth(workingDate.getMonth() + 1);
} while (!(workingDate.getMonth() === now.getMonth() && workingDate.getFullYear() === now.getFullYear()))

export const allColumns = [
    { accessor: 'enrolmentId', Header: 'Enrolment Id', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'fullName', Header: 'Full Name', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'email', Header: 'Email', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_DYNAMIC },
    { accessor: 'fund', Header: 'Wave', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'providerName', Header: 'Provider', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'courseName', Header: 'Course', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'cohortName', Header: 'Cohort', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    { accessor: 'contractSignedAt', Header: 'Contract Signed At', gridFilterType: GRID_FILTER_TYPE_DATE },
    { accessor: 'completedAt', Header: 'Completed At', gridFilterType: GRID_FILTER_TYPE_DATE },
    { accessor: 'status', Header: 'Status', gridFilterType: GRID_FILTER_TYPE_BACKEND_TEXT_STANDARD },
    {
        id: 'sumOfFixedTransactions',
        accessor: (row) => (row.sumOfFixedTransactions / 100).toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP',
            useGrouping: false,
        }),
        Header: 'Sum of Payments',
        disableSortBy: true,
        disableFilters: true,
        tooltipMessage: 'This column reflects the payments made up until the end of the previous month. Any payments for the current month can be viewed in the \'Monthly claim\' download.',
    },
];

const columnsProviderDoesNotSeeByKey = [
    'enrolmentId',
    'providerName',
];

export const cecColumns = allColumns;
export const providerColumns = allColumns.filter(column => !columnsProviderDoesNotSeeByKey.includes(column.id || column.accessor));

const {
    hidePaymentsGridColumn,
    showPaymentsGridColumn,
    showAllPaymentsGridColumns,
    resetPaymentsGridFilterAndSort,
    clearPaymentsGridRowsSelected,
    setPaymentsGridAllVisibleRowsSelected,
    setPaymentsGridCurrentPage,
} = deltas.actionCreators;

const {
    downloadTransactions,
    downloadMonthlyClaim,
    downloadFinanceReport,
    reloadPayments,
} = signals.actionCreators;

const getAreAllVisibleRowsSelected = createSelector(
    state => state.paymentsGrid.filteredSortedData,
    state => state.paymentsGrid.selectedRows,
    (filteredSortedData, selectedRows) => filteredSortedData.map(row => row.id).every(id => selectedRows.includes(id)));

const getBaseColumns = createSelector(
    state => state.role,
    role => (roles.CEC === role) ? cecColumns : providerColumns,
);

const getColumnsToShow = createSelector(
    state => state.paymentsGrid.columnsToHide,
    getBaseColumns,
    (columnsToHide, baseColumns) => baseColumns.filter(column => !columnsToHide.includes(column.id || column.accessor)),
);

const mapStateToProps = state => ({
    isCec: roles.CEC === state.role,
    columnsToHide: state.paymentsGrid.columnsToHide,
    baseColumns: getBaseColumns(state),
    columnsToShow: getColumnsToShow(state),
    allVisibleRowsSelected: getAreAllVisibleRowsSelected(state),
    isLoadingDownloadAllTransactions: selectIsLoadingDownloadAllTransactions(state),
    isLoadingDownloadMonthlyClaim: selectIsLoadingDownloadMonthlyClaim(state),
    isLoadingDownloadFinanceReport: selectIsLoadingDownloadFinanceReport(state),
    paymentsPaginationData: state.data.paymentsPaginationData,
    currentPage: state.paymentsGrid.currentPage,
});

const mapDispatchToProps = (dispatch) => ({
    hidePaymentsGridColumn: column => dispatch(hidePaymentsGridColumn(column)),
    showPaymentsGridColumn: column => dispatch(showPaymentsGridColumn(column)),
    showAllPaymentsGridColumns: () => dispatch(showAllPaymentsGridColumns()),
    resetPaymentsGridFilterAndSort: () => dispatch(resetPaymentsGridFilterAndSort()),
    downloadTransactions: (year, month) => dispatch(downloadTransactions(year, month)),
    downloadMonthlyClaim: () => dispatch(downloadMonthlyClaim()),
    downloadFinanceReport: () => dispatch(downloadFinanceReport()),
    clearPaymentsGridRowsSelected: () => dispatch(clearPaymentsGridRowsSelected()),
    reloadPayments: page => dispatch(reloadPayments(page)),
    setPaymentsGridAllVisibleRowsSelected: () => dispatch(setPaymentsGridAllVisibleRowsSelected()),
    setPaymentsGridCurrentPage: page => dispatch(setPaymentsGridCurrentPage(page)),
});

const PaymentsSection = ({isCec, resetPaymentsGridFilterAndSort, hidePaymentsGridColumn, showPaymentsGridColumn, showAllPaymentsGridColumns, downloadTransactions, downloadMonthlyClaim, downloadFinanceReport, baseColumns, columnsToHide, columnsToShow, isLoadingDownloadAllTransactions, isLoadingDownloadMonthlyClaim, isLoadingDownloadFinanceReport, reloadPayments, setPaymentsGridCurrentPage, paymentsPaginationData, currentPage}) => {

    const formatOptionLabel = ({ value, label }) => (
        <div>
            <CheckboxField
                label={label}
                checked={!columnsToHide.includes(value) && 'ALL' !== value }
            />
        </div>
    );

    return <React.Fragment>
        <PaymentProcessesPollingAlertMessage />
        <div className="d-block d-md-flex mb-3 mt-4">
            <div className="col-md-3 position-relative px-0 no-padding me-2">
                <SelectInput
                    id="o-columns"
                    placeholder="Hide and show columns"
                    isMulti={true}
                    overrideOnChange={(selectedOptions, {option: {value}}) => {
                        if (value === 'ALL') {
                            showAllPaymentsGridColumns();
                            return;
                        }

                        !columnsToHide.includes(value)
                            ? hidePaymentsGridColumn(value)
                            : showPaymentsGridColumn(value);
                    }}
                    options={
                        (
                            0 === columnsToHide.length
                                ? []
                                : [{label: 'Show All', value: 'ALL'}]
                        ).concat(
                            baseColumns.map(({id, accessor, Header}) => ({label: Header, value: id || accessor}))
                        )
                    }
                    {...{formatOptionLabel}}
                    styles={
                        {
                            container: (provided) => ({
                                ...provided,
                                height: '100%',
                            }),
                            control: (provided) => ({
                                ...provided,
                                height: '100%',
                            })
                        }
                    }
                />
            </div>
            <div className="text-justify mt-3 mt-md-auto ms-md-auto mb-n2">
                <button className="cec-btn cec-btn-secondary-teal me-2 mb-2" data-cy="payments--reset-table-button"
                    onClick={() => {
                        resetPaymentsGridFilterAndSort();
                        reloadPayments();
                    }}
                >
                    Reset table
                </button>
                <DownloadButton
                    buttonText='Download all transactions'
                    isLoading={isLoadingDownloadAllTransactions}
                    className='cec-btn cec-btn-primary-teal mt-auto ms-auto dropdown-toggle me-2 mb-2'
                    data-cy="download-all-transactions-button"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                />
                <div className="dropdown-menu dropdown-menu-end">
                    <a className="dropdown-item link-teal-to-gray underline-0"
                       data-cy="download-all-transactions-selector"
                       onClick={(e) => {
                           e.preventDefault();
                           downloadTransactions();
                       }}
                       href={'#'}
                    >
                        All transactions
                    </a>
                    <div className="dropdown-divider" />
                    <div className="y-scrollable-menu">
                        {
                            allTransactionsDownloadDates.map((date, index) =>
                                <a className="dropdown-item link-teal-to-gray underline-0"
                                   data-cy="download-transactions-date-selector"
                                   onClick={(e) => {
                                       e.preventDefault();
                                       downloadTransactions(date.getFullYear(), date.getMonth() + 1);
                                   }}
                                   href={'#'}
                                   key={'atd-'+index}
                                >
                                    {date.toLocaleDateString(undefined, {month: 'short', year: 'numeric'})}
                                </a>
                            )
                        }
                    </div>
                </div>
                <DownloadButton
                    buttonText='Download monthly claim'
                    isLoading={isLoadingDownloadMonthlyClaim}
                    className='cec-btn cec-btn-primary-teal me-2 mb-2'
                    data-cy="download-monthly-claim-button"
                    onClick={downloadMonthlyClaim}
                />
                {
                    isCec &&
                    <DownloadButton
                        buttonText='Download finance report'
                        isLoading={isLoadingDownloadFinanceReport}
                        className="cec-btn cec-btn-primary-teal mb-2"
                        data-cy="download-finance-report-button"
                        onClick={downloadFinanceReport}
                    />
                }
            </div>
        </div>
        <PaymentsGrid columns={columnsToShow}/>
        <div className="d-block d-md-flex justify-content-end mb-3 mt-4">
            <ThemeProvider>
            <OffsetPagination
                id="cec-pagination"
                aria-label="Payments pagination"
                totalPages={paymentsPaginationData.totalPages}
                currentPage={currentPage}
                onChange={(currentPage) => {
                    setPaymentsGridCurrentPage(currentPage);
                    reloadPayments();
                }}
                labels={paginationLabels}
            />
            </ThemeProvider>
        </div>
    </React.Fragment>;
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentsSection);
